import { runInChunks } from '@/lib/fetchAllPages';
import type {
  ShippingMappingOverrideItem,
  ShippingMappingOverridesBulkBody,
  ShippingMappingOverridesBulkResponse,
  ShippingMappingOverridesBulkRowResult,
} from './types';

/** The most items merchant-service takes in one bulk request; it rejects a larger one whole. */
export const SHIPPING_MAPPING_BULK_MAX = 10;

type BulkWrite = (body: ShippingMappingOverridesBulkBody) => Promise<ShippingMappingOverridesBulkResponse>;

export interface ReplaceShippingMappingOverridesArgs {
  /** The merchant whose overrides are replaced: the plugin's. */
  merchantId: string;
  /**
   * The overrides the list shows. Each of the merchant's own that the body names again is
   * updated, and the rest of its own are deleted.
   */
  existing: ShippingMappingOverrideItem[];
  /** The whole new state. */
  body: ShippingMappingOverridesBulkBody;
  createBulk: BulkWrite;
  updateBulk: BulkWrite;
  deleteOne: (id: string) => Promise<unknown>;
}

interface Write {
  /** The item's position in the body, which the response keeps. */
  index: number;
  item: ShippingMappingOverrideItem;
}

interface Plan {
  updates: Write[];
  creates: Write[];
  deletes: string[];
}

/** A legacy all-facilities override can carry no facility; merchant-service now stores 'All'. */
function isLegacyAllFacilities(facilityId: string | null | undefined): boolean {
  return facilityId == null || facilityId === '';
}

/** merchant-service keeps one override per merchant, vendor, facility and group. */
function keyOf(item: ShippingMappingOverrideItem): string {
  const facility = isLegacyAllFacilities(item.facilityId) ? 'All' : item.facilityId;
  return `${facility}\u0000${item.shippingGroupId || ''}`;
}

/** How many of its facility and group are in the shape merchant-service now stores. */
function storedShape(override: ShippingMappingOverrideItem): number {
  return Number(!isLegacyAllFacilities(override.facilityId)) + Number(override.shippingGroupId !== '');
}

/**
 * Pairs each new item with the merchant's own override that holds its key.
 *
 * Another merchant's overrides are left alone. For a sub-account with none of its own,
 * merchant-service lists the parent's instead: updating one would move it off the parent, and
 * deleting one would take it from the parent and from every sub-account that inherits it.
 *
 * A key can still hold a legacy override beside the current one: a blank group beside none
 * (orchestrator reads the null one first), or no facility beside 'All'. The current one is
 * paired: merchant-service writes the stored shape, so updating the legacy one would collide
 * with it on the unique index.
 */
function planReplacement(
  existing: ShippingMappingOverrideItem[],
  items: ShippingMappingOverrideItem[],
  merchantId: string,
): Plan {
  const own = existing.filter((o) => o.merchantId === merchantId);
  const byKey = new Map<string, ShippingMappingOverrideItem>();
  for (const override of own) {
    if (!override.id) continue;
    const held = byKey.get(keyOf(override));
    if (!held || storedShape(override) > storedShape(held)) byKey.set(keyOf(override), override);
  }
  const paired = new Set<string>();
  const updates: Write[] = [];
  const creates: Write[] = [];
  items.forEach((item, index) => {
    const match = byKey.get(keyOf(item));
    if (match?.id && !paired.has(match.id)) {
      paired.add(match.id);
      updates.push({ index, item: { ...item, id: match.id } });
    } else {
      creates.push({ index, item });
    }
  });
  const deletes = own.flatMap((o) => (o.id && !paired.has(o.id) ? [o.id] : []));
  return { updates, creates, deletes };
}

function unconfirmed(): ShippingMappingOverridesBulkRowResult {
  return { status: 500, response: { errors: [{ errorMessage: 'The server returned no result for this row' }] } };
}

async function writeInBatches(
  writes: Write[],
  send: BulkWrite,
  results: ShippingMappingOverridesBulkRowResult[],
): Promise<void> {
  const batches: Write[][] = [];
  for (let i = 0; i < writes.length; i += SHIPPING_MAPPING_BULK_MAX) {
    batches.push(writes.slice(i, i + SHIPPING_MAPPING_BULK_MAX));
  }
  await runInChunks(batches, async (batch) => {
    const response = await send({ bulk: batch.map((w) => w.item) });
    batch.forEach((w, i) => {
      // merchant-service answers every item with a status. A row the response leaves without
      // one is unconfirmed, so it reads as rejected: in the error report, and to the delete guard.
      const result = response.bulk?.[i];
      results[w.index] = typeof result?.status === 'number' ? result : unconfirmed();
    });
  });
}

/**
 * Replaces the merchant's own overrides in `existing` with `body`. The writes come first and
 * update an override in place wherever the body keeps its key, so an item the server rejects
 * leaves the override it would have replaced as it was, instead of deleted with nothing
 * written back. Only once every item is written are the overrides the body leaves out
 * deleted, five at a time; a failed request stops what follows.
 *
 * The response lines up with `body.bulk`, one result per item.
 */
export async function replaceShippingMappingOverrides(
  args: ReplaceShippingMappingOverridesArgs,
): Promise<ShippingMappingOverridesBulkResponse> {
  const { updates, creates, deletes } = planReplacement(args.existing, args.body.bulk, args.merchantId);
  const results: ShippingMappingOverridesBulkRowResult[] = args.body.bulk.map(() => ({}));
  await writeInBatches(updates, args.updateBulk, results);
  await writeInBatches(creates, args.createBulk, results);
  // A rejected item leaves part of the file unwritten, and the overrides the file drops may be
  // what that part replaces: a facility moved from Default into a group whose create failed
  // would be left with no mapping in either. They stay until a whole file is written.
  if (results.some((r) => r.status === undefined || r.status >= 400)) return { bulk: results };
  await runInChunks(deletes, (id) => args.deleteOne(id));
  return { bulk: results };
}
